import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFarm } from '../../contexts/FarmContext';
import { BuildingOfficeIcon, MagnifyingGlassIcon, PlusIcon, PencilSquareIcon, TrashIcon, UserGroupIcon, PauseCircleIcon, PlayCircleIcon } from '@heroicons/react/24/outline';
import { recordsAPI } from '../../services/api';

// Skeleton loader component for better perceived performance
const FarmCardSkeleton = () => (
  <div className="border rounded-xl p-4 bg-white shadow-sm animate-pulse">
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <div className="p-2 rounded-md bg-gray-200 mr-3 h-9 w-9"></div>
        <div>
          <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-24"></div>
        </div>
      </div>
    </div>
    <div className="mt-2 h-4 bg-gray-200 rounded w-28"></div>
    <div className="mt-3 grid grid-cols-2 gap-2">
      <div className="h-8 bg-gray-200 rounded"></div>
      <div className="h-8 bg-gray-200 rounded"></div>
    </div>
  </div>
);

const FarmRowSkeleton = () => (
  <tr className="bg-white animate-pulse">
    <td className="px-4 py-3">
      <div className="flex items-center">
        <div className="p-2 rounded-md bg-gray-200 mr-3 h-9 w-9"></div>
        <div>
          <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
          <div className="h-3 bg-gray-200 rounded w-20"></div>
        </div>
      </div>
    </td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
    <td className="px-4 py-3 text-right">
      <div className="inline-flex items-center gap-2">
        <div className="h-7 bg-gray-200 rounded w-16"></div>
        <div className="h-7 bg-gray-200 rounded w-20"></div>
        <div className="h-7 bg-gray-200 rounded w-16"></div>
      </div>
    </td>
  </tr>
);

// The shop, its products and its customer list are removed by database cascade
// even though nothing in the delete code mentions them. The warning has to say
// so, or an owner agrees to lose records and loses their sales channel too.
const deleteWarning = (name) => `You are about to permanently delete the farm "${name}".

This will permanently remove:
- All flocks (cohorts)
- All activities (including reminders)
- All records (sales, expenses, eggs, feed, stock, mortality, health)
- The farm shop, its products and its customer list
- All notifications and feedback linked to this farm

Farms with shop orders or payout records cannot be deleted.

This action cannot be undone.

To confirm, type the farm name exactly: ${name}`;

const farmEnabled = (farm) => farm && farm.isActive !== false;

const disableWarning = (name) => `Disable "${name}"?

It will leave the dashboard, activity reminders, and the public shop. All flocks and records stay. You can enable it again later.`;

const Farms = () => {
  const { farms, currentFarm, setCurrentFarm, deleteFarm, updateFarm, setFarmStatus, loading } = useFarm();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [farmBirds, setFarmBirds] = useState({});
  const [editingId, setEditingId] = useState('');
  const [editValues, setEditValues] = useState({ name: '', location: '' });
  const [busyId, setBusyId] = useState('');

  const filtered = useMemo(() => {
    let list = farms || [];
    if (statusFilter === 'active') list = list.filter(farmEnabled);
    if (statusFilter === 'disabled') list = list.filter((f) => !farmEnabled(f));
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((f) =>
      [f.name, f.location, f.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [farms, query, statusFilter]);

  // Load total birds per farm
  React.useEffect(() => {
    const load = async () => {
      try {
        const arr = (filtered || []).filter(farmEnabled);
        const results = await Promise.all(arr.map(async (f) => {
          try {
            const res = await recordsAPI.getBirds(f.id);
            return [f.id, res.data || null];
          } catch (e) {
            return [f.id, null];
          }
        }));
        const map = {};
        results.forEach(([id, data]) => { map[id] = data; });
        setFarmBirds(map);
      } catch {}
    };
    load();
  }, [filtered]);

  // Show loading skeletons while farms are being fetched
  if (loading && (!farms || farms.length === 0)) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Your Farms</h1>
            <p className="text-gray-600">Loading your farms...</p>
          </div>
        </div>
        
        {/* Mobile skeleton cards */}
        <div className="space-y-4 md:hidden">
          {[1, 2, 3].map((i) => <FarmCardSkeleton key={i} />)}
        </div>
        
        {/* Desktop skeleton table */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm hidden md:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Farm</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Total Birds</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {[1, 2, 3].map((i) => <FarmRowSkeleton key={i} />)}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!farms || farms.length === 0) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-2xl shadow-xl p-8 text-center text-white">
          <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-6">
            <BuildingOfficeIcon className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Create Your First Farm</h2>
          <p className="text-green-100 mb-8 max-w-md mx-auto">
            Get started by setting up your first poultry farm. Track birds, activities, and grow your business.
          </p>
          <Link
            to="/farm/setup"
            className="inline-flex items-center px-6 py-3 bg-white text-green-700 rounded-xl hover:bg-green-50 transition-colors font-semibold"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Farm
          </Link>
        </div>
      </div>
    );
  }

  const totalBirds = Object.values(farmBirds).reduce((sum, data) => sum + (data?.netBirds || 0), 0);
  const activeCount = (farms || []).filter(farmEnabled).length;
  const disabledCount = (farms || []).length - activeCount;

  const toggleStatus = async (farm) => {
    if (farm.currentUserRole !== 'owner') return;
    const enabling = !farmEnabled(farm);
    const ok = window.confirm(enabling
      ? `Enable "${farm.name}"? It will show on the dashboard again and count toward your farm limit.`
      : disableWarning(farm.name));
    if (!ok) return;
    setBusyId(farm.id);
    try {
      await setFarmStatus(farm.id, enabling);
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
              <BuildingOfficeIcon className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Your Farms</h1>
              <p className="text-green-100">Select a farm to work on, or create a new one</p>
            </div>
          </div>
          <Link 
            to="/farm/setup" 
            className="inline-flex items-center px-5 py-2.5 bg-white text-green-700 rounded-xl hover:bg-green-50 transition-colors font-medium"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New Farm
          </Link>
        </div>
        
        {/* Stats */}
        <div className="relative mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{activeCount}</p>
            <p className="text-green-200 text-xs">Active farms</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{totalBirds.toLocaleString()}</p>
            <p className="text-green-200 text-xs">Total Birds</p>
          </div>
          {disabledCount > 0 && (
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center col-span-2 sm:col-span-1">
              <p className="text-2xl font-bold">{disabledCount}</p>
              <p className="text-green-200 text-xs">Disabled</p>
            </div>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search farms..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
            />
          </div>
          {disabledCount > 0 && (
            <div className="flex rounded-xl border border-gray-200 overflow-hidden text-xs font-medium">
              {[
                { id: 'all', label: 'All' },
                { id: 'active', label: 'Active' },
                { id: 'disabled', label: 'Disabled' }
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setStatusFilter(opt.id)}
                  className={`px-3 py-2 ${statusFilter === opt.id ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile cards */}
      <div className="space-y-4 md:hidden">
        {filtered.map((farm) => {
          const enabled = farmEnabled(farm);
          return (
          <div key={farm.id} className={`border rounded-xl p-4 bg-white shadow-sm ${!enabled ? 'opacity-80' : ''} ${currentFarm?.id === farm.id ? 'ring-1 ring-primary-500' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`p-2 rounded-md mr-3 ${enabled ? 'bg-blue-50' : 'bg-gray-100'}`}>
                  <BuildingOfficeIcon className={`h-5 w-5 ${enabled ? 'text-blue-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  {editingId === farm.id ? (
                    <>
                      <input className="input mb-1" value={editValues.name} onChange={(e)=>setEditValues(p=>({...p,name:e.target.value}))} />
                      <input className="input" value={editValues.location} onChange={(e)=>setEditValues(p=>({...p,location:e.target.value}))} />
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        {enabled ? (
                          <button className="font-medium text-left text-primary-700 underline" onClick={()=> navigate(`/farm/${farm.id}/flocks`)}>{farm.name}</button>
                        ) : (
                          <span className="font-medium text-gray-700">{farm.name}</span>
                        )}
                        {!enabled && <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Disabled</span>}
                      </div>
                      <div className="text-xs text-gray-500">{farm.location || '—'}</div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-700">Total birds: <span className="font-medium">{enabled ? (farmBirds[farm.id]?.netBirds ?? '—') : '—'}</span></div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {enabled && (
                <>
                  <button
                    className={`px-3 py-2 rounded-md border text-xs transition ${currentFarm?.id === farm.id ? 'bg-primary-600 border-primary-600 text-white' : 'border-gray-300 text-gray-700'}`}
                    onClick={() => { setCurrentFarm(farm); navigate('/dashboard'); }}
                  >{currentFarm?.id === farm.id ? 'Working here' : 'Work here'}</button>
                  <Link to={`/farm/${farm.id}/flocks`} className="px-3 py-2 rounded-md border border-primary-600 text-primary-700 text-xs text-center">View Flocks</Link>
                </>
              )}
              {editingId === farm.id ? (
                <>
                  <button className="px-3 py-2 rounded-md border border-primary-600 text-primary-700 text-xs" onClick={async ()=>{ await updateFarm(farm.id, { name: editValues.name, location: editValues.location }); setEditingId(''); }}>Save</button>
                  <button className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 text-xs" onClick={()=> setEditingId('')}>Cancel</button>
                </>
              ) : (
                <button className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 text-xs" onClick={()=> { setEditingId(farm.id); setEditValues({ name: farm.name || '', location: farm.location || ''}); }}>Edit</button>
              )}
              {enabled && <button className="px-3 py-2 rounded-md border border-secondary-600 text-secondary-700 text-xs" onClick={()=> navigate(`/farm/${farm.id}#add-flock`)}>Add Flock</button>}
              {farm.currentUserRole === 'owner' && (
                <button
                  className={`px-3 py-2 rounded-md border text-xs ${enabled ? 'border-amber-300 text-amber-800' : 'border-green-600 text-green-700'}`}
                  disabled={busyId === farm.id}
                  onClick={() => toggleStatus(farm)}
                >
                  {busyId === farm.id ? 'Saving…' : (enabled ? 'Disable' : 'Enable')}
                </button>
              )}
              <button className="px-3 py-2 rounded-md border border-gray-300 text-red-700 text-xs" onClick={async ()=>{
                const ok = window.confirm(deleteWarning(farm.name));
                if (!ok) return;
                const typed = window.prompt('Type the farm name to confirm deletion:');
                if ((typed || '').trim() !== String(farm.name || '').trim()) { alert('Confirmation failed. Farm was not deleted.'); return; }
                const result = await deleteFarm(farm.id);
                if (result && !result.success && result.error) alert(result.error);
              }}>Delete</button>
            </div>
          </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm hidden md:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Farm</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Location</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Total Birds</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map((farm) => {
              const enabled = farmEnabled(farm);
              return (
              <tr key={farm.id} className={`${!enabled ? 'bg-gray-50' : currentFarm?.id === farm.id ? 'bg-primary-50/40' : 'bg-white'} hover:bg-gray-50 transition`}>                
                <td className="px-4 py-3 text-sm text-gray-900">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-md mr-3 ${enabled ? 'bg-blue-50' : 'bg-gray-100'}`}>
                      <BuildingOfficeIcon className={`h-5 w-5 ${enabled ? 'text-blue-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      {editingId === farm.id ? (
                        <input className="input" value={editValues.name} onChange={(e)=>setEditValues(p=>({...p,name:e.target.value}))} />
                      ) : enabled ? (
                        <button className="font-medium text-primary-700 underline" onClick={()=> navigate(`/farm/${farm.id}/flocks`)}>{farm.name}</button>
                      ) : (
                        <span className="font-medium text-gray-700">{farm.name}</span>
                      )}
                      <div className="text-xs text-gray-500">ID: {farm.id.slice(0, 8)}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {editingId === farm.id ? (
                    <input className="input" value={editValues.location} onChange={(e)=>setEditValues(p=>({...p,location:e.target.value}))} />
                  ) : (
                    farm.location || '—'
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${enabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                    {enabled ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{enabled ? (farmBirds[farm.id]?.netBirds ?? '—') : '—'}</td>
                <td className="px-4 py-3 text-sm text-right">
                  <div className="inline-flex flex-wrap items-center justify-end gap-2">
                    {enabled && (
                      <>
                        <button
                          className={`px-3 py-1.5 rounded-md border text-xs transition ${currentFarm?.id === farm.id ? 'bg-primary-600 border-primary-600 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                          onClick={() => {
                            setCurrentFarm(farm);
                            navigate('/dashboard');
                          }}
                        >
                          {currentFarm?.id === farm.id ? 'Working here' : 'Work here'}
                        </button>
                        <Link to={`/farm/${farm.id}/flocks`} className="px-3 py-1.5 rounded-md border border-primary-600 text-primary-700 text-xs hover:bg-primary-50">View Flocks</Link>
                      </>
                    )}
                    {editingId === farm.id ? (
                      <>
                        <button className="px-3 py-1.5 rounded-md border border-primary-600 text-primary-700 text-xs" onClick={async ()=>{ await updateFarm(farm.id, { name: editValues.name, location: editValues.location }); setEditingId(''); }}>Save</button>
                        <button className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 text-xs" onClick={()=> setEditingId('')}>Cancel</button>
                      </>
                    ) : (
                      <button
                        className={`px-3 py-1.5 rounded-md border text-xs inline-flex items-center ${['owner','manager'].includes(farm.currentUserRole) ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'}`}
                        onClick={()=> { if (!['owner','manager'].includes(farm.currentUserRole)) return; setEditingId(farm.id); setEditValues({ name: farm.name || '', location: farm.location || ''}); }}
                        disabled={!['owner','manager'].includes(farm.currentUserRole)}
                      >
                        <PencilSquareIcon className="h-4 w-4 mr-1"/> Edit
                      </button>
                    )}
                    {farm.currentUserRole === 'owner' && (
                      <button
                        className={`px-3 py-1.5 rounded-md border text-xs inline-flex items-center ${enabled ? 'border-amber-300 text-amber-800 hover:bg-amber-50' : 'border-green-600 text-green-700 hover:bg-green-50'}`}
                        disabled={busyId === farm.id}
                        onClick={() => toggleStatus(farm)}
                      >
                        {enabled ? <PauseCircleIcon className="h-4 w-4 mr-1"/> : <PlayCircleIcon className="h-4 w-4 mr-1"/>}
                        {busyId === farm.id ? 'Saving…' : (enabled ? 'Disable' : 'Enable')}
                      </button>
                    )}
                    <button
                      className={`px-3 py-1.5 rounded-md border text-xs inline-flex items-center ${farm.currentUserRole === 'owner' ? 'border-gray-300 text-red-700 hover:bg-red-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'}`}
                      disabled={farm.currentUserRole !== 'owner'}
                      onClick={async ()=>{
                        if (farm.currentUserRole !== 'owner') return;
                        const ok = window.confirm(deleteWarning(farm.name));
                        if (!ok) return;
                        const typed = window.prompt('Type the farm name to confirm deletion:');
                        if ((typed || '').trim() !== String(farm.name || '').trim()) { alert('Confirmation failed. Farm was not deleted.'); return; }
                        const result = await deleteFarm(farm.id);
                        if (result && !result.success && result.error) alert(result.error);
                      }}
                    >
                      <TrashIcon className="h-4 w-4 mr-1"/> Delete
                    </button>
                    {enabled && (
                      <button
                        className={`px-3 py-1.5 rounded-md border text-xs inline-flex items-center ${['owner','manager','editor'].includes(farm.currentUserRole) ? 'border-secondary-600 text-secondary-700 hover:bg-secondary-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'}`}
                        onClick={()=> { if (!['owner','manager','editor'].includes(farm.currentUserRole)) return; navigate(`/farm/${farm.id}#add-flock`);}}
                        disabled={!['owner','manager','editor'].includes(farm.currentUserRole)}
                      >
                        <UserGroupIcon className="h-4 w-4 mr-1"/> Add Flock
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">No farms match your search.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Farms;
